import { useEffect, useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";

import { Button, Check, Field, Select, TextInput, cx } from "../components/ui";
import { ApiError, api } from "../lib/api";
import { useStore } from "../lib/store";
import type { SetupPayload } from "../lib/types";

const SECTIONS = ["FreeSWITCH ESL", "Bitcall SIP", "Fish / DeepL", "Operator"] as const;

/**
 * First-run wizard. ESL host is `freeswitch` unless advanced is toggled.
 * Codecs locked PCMU,PCMA. Dual admin password.
 */
export function Setup() {
  const { state, toast } = useStore();
  const [step, setStep] = useState(0);
  const [advanced, setAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    host: "freeswitch",
    port: "8021",
    eslPassword: "",
    proxy: "gateway.bitcall.io:5060",
    sipUser: "",
    sipPassword: "",
    transport: "udp" as "udp" | "tcp" | "tls",
    register: true,
    fishKey: "",
    voiceId: "9a9cf47702da476aa4629e2506d4a857",
    deeplKey: "",
    operator: "",
    business: "",
    purpose: "account verification",
    admin: "",
    admin2: "",
  });

  useEffect(() => {
    void api.setupStatus().then((status) => {
      setForm((prev) => ({
        ...prev,
        host: status.esl_host || prev.host,
        port: String(status.esl_port ?? prev.port),
        proxy: status.bitcall_proxy || prev.proxy,
        operator: status.operator_name || prev.operator,
        business: status.business_name || prev.business,
        voiceId: status.voice_id || prev.voiceId,
      }));
    }).catch(() => {
      /* first-run may 200 with ready:false only */
    });
  }, []);

  if (state.boot === "ready" && state.setup && state.authenticated) {
    return <Navigate to="/" replace />;
  }
  if (state.boot === "ready" && state.setup && !state.authenticated) {
    return <Navigate to="/login" replace />;
  }

  const patch = (partial: Partial<typeof form>) => setForm((prev) => ({ ...prev, ...partial }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (form.admin !== form.admin2) {
      toast("Admin passwords do not match", "error");
      return;
    }
    if (form.admin.length < 8) {
      toast("Admin password must be at least 8 characters", "error");
      return;
    }
    // Mirror SetupIn (schemas.py): esl_port 1–65535, esl_password min_length=1.
    // No silent `|| 8021` fallback — a typo must not become a different port.
    const port = Number.parseInt(form.port, 10);
    if (!Number.isInteger(port) || port < 1 || port > 65535) {
      toast("ESL port must be a whole number 1–65535", "error");
      return;
    }
    if (!form.eslPassword) {
      toast("ESL password is required", "error");
      return;
    }
    const proxy = form.proxy.trim();
    if (!proxy || /\s/.test(proxy)) {
      toast("Bitcall proxy must be host[:port] with no spaces", "error");
      return;
    }
    const payload: SetupPayload = {
      esl_host: advanced ? form.host.trim() : "freeswitch",
      esl_port: port,
      esl_password: form.eslPassword,
      bitcall_username: form.sipUser.trim(),
      bitcall_password: form.sipPassword,
      bitcall_proxy: proxy,
      fish_api_key: form.fishKey.trim(),
      voice_id: form.voiceId.trim(),
      deepl_api_key: form.deeplKey.trim(),
      operator_name: form.operator.trim(),
      business_name: form.business.trim(),
      admin_password: form.admin,
      admin_password_confirm: form.admin2,
    };
    setBusy(true);
    try {
      await api.submitSetup(payload);
      window.location.assign("/login");
    } catch (err) {
      toast(err instanceof ApiError ? err.detail : "Setup failed", "error");
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-full max-w-2xl flex-col px-6 py-12">
      <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.22em] text-teal">cdialer@ops — setup</div>
      <h1 className="mb-2 text-3xl font-medium tracking-tight">First-run setup</h1>
      <p className="mb-8 max-w-xl text-sm leading-relaxed text-mute">
        Four sections. Codecs locked to PCMU,PCMA. ESL talks to the FreeSWITCH container.
      </p>
      <ol className="mb-8 flex gap-2">
        {SECTIONS.map((label, index) => (
          <li key={label} className="flex-1">
            <button
              type="button"
              onClick={() => setStep(index)}
              className={cx(
                "w-full rounded-lg px-3 py-2 text-left text-xs tracking-wide hairline",
                index === step ? "bg-teal/15 text-teal" : "text-mute",
              )}
            >
              <span className="font-mono text-[10px] text-zinc-600">0{index + 1}</span>
              <div>{label}</div>
            </button>
          </li>
        ))}
      </ol>
      <form onSubmit={(event) => void submit(event)} className="space-y-5 rounded-2xl border border-border bg-navy p-6">
        {step === 0 ? (
          <>
            <Field label="ESL host" hint="Readonly unless advanced is on. Docker service name is freeswitch.">
              <TextInput
                value={form.host}
                readOnly={!advanced}
                onChange={(event) => patch({ host: event.target.value })}
              />
            </Field>
            <Check label="Advanced — edit ESL host" checked={advanced} onChange={setAdvanced} />
            <Field label="ESL port">
              <TextInput value={form.port} onChange={(event) => patch({ port: event.target.value })} />
            </Field>
            <Field label="ESL password">
              <TextInput
                type="password"
                autoComplete="new-password"
                value={form.eslPassword}
                onChange={(event) => patch({ eslPassword: event.target.value })}
              />
            </Field>
          </>
        ) : null}
        {step === 1 ? (
          <>
            <Field label="Proxy">
              <TextInput value={form.proxy} onChange={(event) => patch({ proxy: event.target.value })} />
            </Field>
            <Field label="Username">
              <TextInput value={form.sipUser} onChange={(event) => patch({ sipUser: event.target.value })} />
            </Field>
            <Field label="Password">
              <TextInput
                type="password"
                value={form.sipPassword}
                onChange={(event) => patch({ sipPassword: event.target.value })}
              />
            </Field>
            <Field label="Transport">
              <Select
                value={form.transport}
                onChange={(event) => patch({ transport: event.target.value as typeof form.transport })}
              >
                <option value="udp">udp</option>
                <option value="tcp">tcp</option>
                <option value="tls">tls</option>
              </Select>
            </Field>
            <Field label="Codecs">
              <TextInput value="PCMU,PCMA" readOnly />
            </Field>
            <Check label="Register" checked={form.register} onChange={(register) => patch({ register })} />
          </>
        ) : null}
        {step === 2 ? (
          <>
            <Field label="Fish Audio API key">
              <TextInput
                type="password"
                value={form.fishKey}
                onChange={(event) => patch({ fishKey: event.target.value })}
              />
            </Field>
            <Field label="Voice id" hint="Hannah is the English default. Do not scrape clones.">
              <TextInput value={form.voiceId} onChange={(event) => patch({ voiceId: event.target.value })} />
            </Field>
            <Field label="DeepL API key" hint="Optional. Empty keeps English path.">
              <TextInput
                type="password"
                value={form.deeplKey}
                onChange={(event) => patch({ deeplKey: event.target.value })}
              />
            </Field>
          </>
        ) : null}
        {step === 3 ? (
          <>
            <Field label="Operator name">
              <TextInput value={form.operator} onChange={(event) => patch({ operator: event.target.value })} />
            </Field>
            <Field label="Business">
              <TextInput value={form.business} onChange={(event) => patch({ business: event.target.value })} />
            </Field>
            <Field label="Default purpose">
              <TextInput value={form.purpose} onChange={(event) => patch({ purpose: event.target.value })} />
            </Field>
            <Field label="Admin password">
              <TextInput
                type="password"
                autoComplete="new-password"
                value={form.admin}
                onChange={(event) => patch({ admin: event.target.value })}
              />
            </Field>
            <Field label="Confirm admin password">
              <TextInput
                type="password"
                autoComplete="new-password"
                value={form.admin2}
                onChange={(event) => patch({ admin2: event.target.value })}
              />
            </Field>
          </>
        ) : null}
        <div className="flex items-center justify-between pt-2">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep((n) => Math.max(0, n - 1))}>
            Back
          </Button>
          {step < 3 ? (
            <Button variant="primary" onClick={() => setStep((n) => Math.min(3, n + 1))}>
              Continue
            </Button>
          ) : (
            <Button type="submit" variant="primary" disabled={busy}>
              Finish setup
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
